import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, SESSION_EXPIRED_EVENT } from './api';

export interface User {
  id: string;
  email: string;
  display_name: string;
  staff_nickname?: string | null;
  staff_signature_title?: string | null;
  avatar_url?: string;
  avatar_retry_after_ms?: number;
  plan_code: string;
  storage_quota_bytes: string;
  log_quota_bytes: string;
  storage_used_bytes: string;
  log_used_bytes: string;
  retention_days: number;
  subscription_status: string;
  subscription_period_end: string | null;
  has_subscription?: boolean;
  storage_grace_reason: 'payment_failed' | 'subscription_expired' | 'plan_downgrade' | null;
  storage_grace_deadline: string | null;
  file_requests_count?: number | string;
  fair_use_grace_deadline?: string | null;
  fair_use_paused_at?: string | null;
  pending_plan_code: string | null;
  pending_plan_effective_at: string | null;
  signup_plan_reason?: 'network_free' | 'discord_free' | 'device_free' | null;
  totp_enabled?: boolean;
  has_password?: boolean;
  discord_linked?: boolean;
  admin_permissions: string[];
  current_session_id: string | null;
  workspace?: {
    owner_user_id: string;
    role: 'owner' | 'admin' | 'member' | 'viewer';
    permissions: {
      filesRead: boolean;
      filesWrite: boolean;
      filesDelete: boolean;
      filesPrivacy: boolean;
      foldersCreate: boolean;
      foldersDelete: boolean;
      logsRead: boolean;
      logsWrite: boolean;
      credentials: boolean;
      members: boolean;
      billing: boolean;
      folderScope: string[] | null;
    };
  } | null;
}

interface AuthContextValue {
  user: User | undefined;
  loading: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['me'],
    queryFn: () => api<{ user: User }>('/auth/me').then((result) => result.user),
    retry: false,
    staleTime: 30_000,
  });
  const clearUserState = () => {
    void client.cancelQueries();
    client.setQueryData(['me'], null);
    client.removeQueries({ queryKey: ['me'] });
    client.clear();
  };

  const logoutMutation = useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      clearUserState();
    },
  });

  useEffect(() => {
    const expireSession = () => {
      clearUserState();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expireSession);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expireSession);
  }, [client]);

  return (
    <AuthContext.Provider
      value={{
        user: query.data ?? undefined,
        loading: query.isLoading,
        logout: async () => {
          try {
            await logoutMutation.mutateAsync();
          } finally {
            clearUserState();
          }
        },
        refresh: async () => {
          await query.refetch();
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
